/**
 * Forms Modal Handler
 * Handles opening modal dialogs for Shopify Forms App inline forms
 * Supports links/buttons with href="#form-modal-{FORM_ID}" or data-form-modal="{FORM_ID}"
 */

class FormsModalHandler {
  modal = null;
  formId = null;
  scrollbarWidth = 0;

  constructor() {
    this.init();
  }

  getScrollbarWidth() {
    // Method 1: Calculate from window and document dimensions (most reliable)
    const scrollbarWidth = globalThis.innerWidth - document.documentElement.clientWidth;

    if (scrollbarWidth > 0) {
      return scrollbarWidth;
    }

    // Method 2: Fallback - create a temporary div to measure scrollbar width
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    outer.style.msOverflowStyle = 'scrollbar';
    outer.style.width = '100px';
    outer.style.position = 'absolute';
    outer.style.top = '-9999px';
    document.body.appendChild(outer);

    const inner = document.createElement('div');
    inner.style.width = '100%';
    outer.appendChild(inner);

    const calculatedWidth = outer.offsetWidth - inner.offsetWidth;

    outer.remove();

    return calculatedWidth;
  }

  compensateScrollbar() {
    // Calculate scrollbar width if not already calculated
    if (this.scrollbarWidth === 0) {
      this.scrollbarWidth = this.getScrollbarWidth();
    }

    // Add padding to body to compensate for scrollbar
    if (this.scrollbarWidth > 0) {
      document.body.style.paddingRight = `${this.scrollbarWidth}px`;
    }
  }

  removeScrollbarCompensation() {
    // Remove padding compensation
    document.body.style.paddingRight = '';
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Find the modal element
    this.modal = document.getElementById('FormsModal');
    if (!this.modal) {
      console.warn('FormsModal: Modal element not found');
      return;
    }

    // Setup close button handler
    const closeButton = this.modal.querySelector('#ModalClose-FormsModal');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.closeModal());
    }

    // Listen for modal close events (when clicking outside, pressing ESC, or clicking close button)
    // This event is dispatched by ModalDialog.hide()
    this.modalClosedHandler = () => {
      // Remove padding compensation when modal is closed
      // Use setTimeout to ensure it happens after the modal is fully closed
      setTimeout(() => {
        if (this.modal && !this.modal.hasAttribute('open')) {
          this.removeScrollbarCompensation();
        }
      }, 0);
    };
    document.body.addEventListener('modalClosed', this.modalClosedHandler);

    // Setup click handlers for all links and buttons
    this.setupClickHandlers();

    // Handle hash changes (for direct links)
    globalThis.addEventListener('hashchange', () => this.handleHashChange());

    // Check initial hash
    if (globalThis.location.hash) {
      this.handleHashChange();
    }

    // Handle Shopify section reloads
    document.addEventListener('shopify:section:load', () => {
      this.setupClickHandlers();
    });
  }

  setupClickHandlers() {
    // Remove existing handlers to avoid duplicates
    document.removeEventListener('click', this.handleClick);

    // Add new handler
    this.handleClick = this.handleClick.bind(this);
    document.addEventListener('click', this.handleClick, true);
  }

  handleClick(event) {
    const target = event.target.closest('a, button');
    if (!target) return;

    // Check for data-form-modal attribute
    const formIdFromData = target.dataset.formModal;
    if (formIdFromData) {
      event.preventDefault();
      this.openModal(formIdFromData, target);
      return;
    }

    // Check for href with #form-modal-{ID} format
    const href = target.getAttribute('href');
    if (href?.startsWith('#form-modal-')) {
      event.preventDefault();
      const formId = href.replace('#form-modal-', '');
      this.openModal(formId, target);
    }
  }

  handleHashChange() {
    const hash = globalThis.location.hash;
    if (hash?.startsWith('#form-modal-')) {
      const formId = hash.replace('#form-modal-', '');
      this.openModal(formId);
    }
  }

  openModal(formId, opener = null) {
    if (!this.modal) {
      console.warn('FormsModal: Modal element not found');
      return;
    }

    this.formId = formId;

    // Compensate for scrollbar before hiding overflow
    this.compensateScrollbar();

    // Use the modal-dialog's show method if available
    if (this.modal.show && typeof this.modal.show === 'function') {
      this.modal.show(opener);
    } else {
      // Fallback: manually open the modal
      this.modal.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');

      // Focus trap
      const dialog = this.modal.querySelector('[role="dialog"]');
      if (dialog) {
        trapFocus(this.modal, dialog);
      }
    }

    // Load form content if needed
    this.loadFormContent(formId);
  }

  loadFormContent(formId) {
    // The form should already be in the modal via the section block
    // But we can trigger any necessary form initialization here
    const formWrapper = document.getElementById('forms-modal-form-wrapper');
    if (formWrapper) {
      // Trigger any form app initialization if needed
      const formAppBlock = formWrapper.querySelector('[data-shopify-form-id]');
      if (formAppBlock) {
        // Form app should handle its own initialization
        // But we can dispatch a custom event if needed
        formWrapper.dispatchEvent(
          new CustomEvent('forms-modal:opened', {
            detail: { formId },
          })
        );
      }
    }
  }

  closeModal() {
    if (!this.modal) return;

    // Use the modal-dialog's hide method if available
    if (this.modal.hide && typeof this.modal.hide === 'function') {
      this.modal.hide();
      // Remove scrollbar compensation after modal hides
      setTimeout(() => {
        this.removeScrollbarCompensation();
      }, 0);
    } else {
      // Fallback: manually close the modal with animation
      // Remove open attribute to trigger closing animation
      this.modal.removeAttribute('open');

      // Wait for animation to complete before removing overflow and compensation
      setTimeout(() => {
        document.body.classList.remove('overflow-hidden');
        this.removeScrollbarCompensation();
        removeTrapFocus();
      }, 300); // Match CSS transition duration
    }

    // Clear hash if it was set by our handler
    const hash = globalThis.location.hash;
    if (hash?.startsWith('#form-modal-')) {
      globalThis.history.replaceState(null, '', globalThis.location.pathname + globalThis.location.search);
    }
  }
}

// Initialize the handler
const formsModalHandler = new FormsModalHandler();

// Export for potential external use
if (typeof globalThis !== 'undefined') {
  globalThis.FormsModalHandler = FormsModalHandler;
  globalThis.formsModalHandler = formsModalHandler;
}
